"use client";

import styles from "@/styles/category/index.module.scss";
import Image from "next/image";
import { useState, useEffect } from "react";
import featuredSectionApi from "@/service/modules/featuredSectionApi";
import type { ProductType } from "@/types/product";
import type {
  CategoryQueryParams,
  FilterOptionItem,
  SidebarFilterItem,
} from "@/types/category";
import dayjs from "dayjs";
import ProductItem from "@/components/global/ProductItem";
import ProductCardFullWidth from "@/components/global/ProductCardFullWidth";
import InfiniteListLoading from "@/components/global/InfiniteListLoading";
import FilterBox from "@/components/category/FilterBox";
import PageTheme from "@/components/global/PageTheme";
import { List, Loading, Skeleton } from "react-vant";
// import { setGtmDataLayer } from "@/utils/gtmDataLayer";
// import { getCookie } from "@/utils/cookie";

interface FeaturedSectionProps {
  type: string;
  title?: string;
  defaultSort?: {
    sortBy?: string;
    sortDirection?: string;
  };
}

const WEEKLY_DEALS_TYPE = "weekly-deals";

// 获取分类列表数据
const getProductList = async (type: string, params: CategoryQueryParams) => {
  try {
    // 清空params中的空值键值对
    const dataObj = Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== null && value !== ""
      )
    );

    const productData = await featuredSectionApi.getSectionList(type, dataObj);

    const responseData = productData.data || {};
    const weeklyDealsPageList = responseData.weeklySales?.pageList;
    const pageList =
      type === WEEKLY_DEALS_TYPE && weeklyDealsPageList
        ? {
            ...weeklyDealsPageList,
            // Weekly Deals 接口返回完整列表，不再继续分页加载。
            total: weeklyDealsPageList.items?.length || 0,
          }
        : responseData.pageList;

    if (pageList?.items?.length) {
      pageList.items.forEach((item: ProductType) => {
        item.imgUrl = item.filename || item.newThumbnail;
      });
    }

    // 对 sidebarFilter 进行排序，将 key 为 "category" 的元素排到最前面
    if (
      responseData.sidebarFilter &&
      Array.isArray(responseData.sidebarFilter)
    ) {
      responseData.sidebarFilter.sort((a: SidebarFilterItem, b: SidebarFilterItem) => {
        if (a.key === "category") return -1;
        if (b.key === "category") return 1;
        return 0;
      });
    }

    return {
      ...responseData,
      pageList,
    };
  } catch (error) {
    console.log(
      "Next.js Error :",
      dayjs().format("YY-MM-DD HH:mm:ss"),
      `  FeaturedSection ${type} Data acquisition failed:`,
      JSON.stringify(error)
    );
  }
};

// type 到标题的映射
const typeTitleMap: Record<string, string> = {
  "top-seller": "Hottest Deals",
  clearance: "Clearance",
  "new-arrival": "New Arrivals",
  "free-shipping": "Free Shipping",
  "weekly-deals": "Weekly Deals",
};

export default function FeaturedSection({
  type,
  title,
  defaultSort,
}: FeaturedSectionProps) {
  // 将传入的 sidebarFilter 存储到本地状态，便于页面重新请求时保持和重新赋值
  const [localSidebarFilter, setLocalSidebarFilter] = useState<SidebarFilterItem[]>([]);
  // 维护请求参数对象（用于 getProductList）
  const [queryParams, setQueryParams] = useState<CategoryQueryParams>({
    page: 1,
    perPage: 16,
    ...(defaultSort &&
      defaultSort.sortBy && {
        sortBy: defaultSort.sortBy,
        sortDirection: defaultSort.sortDirection?.toLowerCase() || "desc",
      }),
  });
  // 产品列表数据
  const [productList, setProductList] = useState<ProductType[]>([]);
  // 是否已加载完所有数据
  const [finished, setFinished] = useState<boolean>(false);
  // 当前页码
  const [currentPage, setCurrentPage] = useState<number>(1);
  // 总数据量
  const [total, setTotal] = useState<number>(0);
  // 筛选/排序时的加载状态
  const [isRefreshing, setIsRefreshing] = useState(false);
  // 添加展示模式状态，默认为网格模式（一行两个商品）
  const [isGridMode, setIsGridMode] = useState(true);
  // 是否正在初始化加载
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // 获取筛选配置
  const getSidebarFilter = async (type: string) => {
    try {
      // 并行请求两个接口
      const [sidebarRes, categoryRes] = await Promise.all([
        featuredSectionApi.getSidebarFilter(type),
        type === WEEKLY_DEALS_TYPE
          ? Promise.resolve(null)
          : featuredSectionApi.getRecommendCategory(type),
      ]);

      const filterBarObject = sidebarRes.data;
      let categoryData = categoryRes?.data;

      // 格式化筛选配置
      if (
        filterBarObject &&
        filterBarObject.sidebarFilter &&
        Array.isArray(filterBarObject.sidebarFilter)
      ) {
        let sidebarFilterFormatter = [...filterBarObject.sidebarFilter];

        // 1. 剔除 key 为 "custom-filtering" 的项
        sidebarFilterFormatter = sidebarFilterFormatter.filter(
          (item) => item.key !== "custom-filtering"
        );

        // 1.5. 如果 categoryData 为空，在过滤之前先检查 sidebarFilterFormatter 中是否已有 category 项且 items 有数据
        if (!(categoryData && Array.isArray(categoryData) && categoryData.length > 0)) {
          const existingCategoryIndex = sidebarFilterFormatter.findIndex(
            (item) => item.key === "category"
          );
          if (
            existingCategoryIndex !== -1 &&
            Array.isArray(sidebarFilterFormatter[existingCategoryIndex].items) &&
            sidebarFilterFormatter[existingCategoryIndex].items.length > 0
          ) {
            // 如果存在且 items 有数据，将 items 赋值给 categoryData，以便第3步能执行
            categoryData = sidebarFilterFormatter[existingCategoryIndex].items;
          }
        }

        // 2. 只保留 price-range、hot-brand
        sidebarFilterFormatter = sidebarFilterFormatter.filter(
          (item) =>
            item.key === "price-range" ||
            item.key === "hot-brand"
        );

        // 3. 如果有分类数据，组装到 category 筛选项中
        if (categoryData && Array.isArray(categoryData) && categoryData.length > 0) {
          // 将 name 转换为 url 格式的辅助函数
          const nameToUrl = (name: string): string => {
            if (!name) return "";
            // 转换为小写，将特殊字符和空格替换为 `-`
            const url = name
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-") // 将非字母数字字符替换为 `-`
              .replace(/^-+|-+$/g, ""); // 去除开头和结尾的 `-`
            return `/${url}/`;
          };

          // 递归映射分类数据的辅助函数
          const mapCategoryItem = (item: FilterOptionItem): FilterOptionItem => {
            const name = item.name || "";
            return {
              categoryID: item.categoryID || "",
              count: 1,
              name: name,
              parent: 1,
              subCategories: [],
              url: nameToUrl(name),
            };
          };

          // 映射 categoryData 为所需格式
          const mappedCategoryData = categoryData.map((item: FilterOptionItem) => mapCategoryItem(item));

          // 在第一条插入 "View All" 元素
          mappedCategoryData.unshift({
            categoryID: "",
            count: 0,
            name: "View All",
            parent: null,
            subCategories: [],
            url: "",
          });

          sidebarFilterFormatter.unshift({
            key: "category",
            title: "Browse",
            items: mappedCategoryData,
          });
        }

        // 4. 增加 popularity 选项
        sidebarFilterFormatter.push({
          key: "popularity",
          title: "Popularity",
        });

        setLocalSidebarFilter(sidebarFilterFormatter);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // 初始化加载筛选配置
  useEffect(() => {
    getSidebarFilter(type);
  }, [type]);

  // 初始化加载数据
  useEffect(() => {
    const loadInitialData = async () => {
      setIsInitialLoading(true);
      // 构建初始请求参数
      const initialParams: CategoryQueryParams = {
        page: 1,
        perPage: 16,
      };

      // 如果有默认排序，添加到参数中
      if (defaultSort && defaultSort.sortBy) {
        initialParams.sortBy = defaultSort.sortBy;
        initialParams.sortDirection =
          defaultSort.sortDirection?.toLowerCase() || "desc";
      }

      // 更新 queryParams 状态
      setQueryParams(initialParams);

      try {
        const data = await getProductList(type, initialParams);
        if (data && data.pageList) {
          setProductList(data.pageList.items || []);
          setCurrentPage(
            data.pageList.currentPage || data.pageList.page || 1
          );
          setTotal(data.pageList.total || 0);
          setFinished(
            (data.pageList.items?.length || 0) >= (data.pageList.total || 0)
          );
        } else {
          setProductList([]);
          setFinished(true);
        }
      } catch (error) {
        console.error("初始化加载失败:", error);
        setProductList([]);
        setTotal(0);
        setFinished(true);
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadInitialData();
  }, [type, defaultSort]); // 当 type 或 defaultSort 变化时重新初始化

  // useEffect(() => {
  //   const g_params = {
  //     event: "productListPage",
  //     remarketing_params: {
  //       ecomm_prodid: "",
  //       ecomm_pagetype: "category",
  //       ecomm_totalvalue: "",
  //     },
  //     params: { userId: null, categoryId: null },
  //     emarketing_params: {
  //       pagetype: "",
  //       productid: "",
  //       productsku: "",
  //       searchterm: "",
  //       category: type || "",
  //       cartinfo: "",
  //       orderid: "",
  //       customerid: getCookie("userCustomerId") || null,
  //       customeremail: localStorage.getItem("userEmail") || null,
  //       orderinfo: "",
  //     },
  //   };

  //   setGtmDataLayer(g_params);
  // }, [type]);

  // 滚动加载更多数据
  const onLoad = async () => {
    if (finished || isInitialLoading) return;

    try {
      const nextPage = currentPage + 1;
      const nextParams: CategoryQueryParams = {
        ...queryParams,
        page: nextPage,
      };

      if (!nextParams.sortBy && defaultSort?.sortBy) {
        nextParams.sortBy = defaultSort.sortBy;
        nextParams.sortDirection =
          defaultSort.sortDirection?.toLowerCase() || "desc";
      }

      // 如果 sortBy 是 bestMatch 且有 defaultSort，替换成 defaultSort
      if (nextParams.sortBy === "bestMatch" && defaultSort?.sortBy) {
        nextParams.sortBy = defaultSort.sortBy;
        nextParams.sortDirection =
          defaultSort.sortDirection?.toLowerCase() || "desc";
      }

      const data = await getProductList(type, nextParams);

      if (
        data &&
        data.pageList &&
        data.pageList.items &&
        data.pageList.items.length > 0
      ) {
        setProductList((prev) => [...prev, ...data.pageList.items]);
        setCurrentPage(nextPage);
        setTotal(data.pageList.total || 0);

        // 检查是否已加载完所有数据
        const loadedCount = productList.length + data.pageList.items.length;
        if (loadedCount >= data.pageList.total) {
          setFinished(true);
        }
      } else {
        setFinished(true);
      }
    } catch (error) {
      console.error("加载更多失败:", error);
      setFinished(true);
    }
  };

  // 根据参数刷新列表（用于筛选/排序）
  const refreshList = async (nextParams: CategoryQueryParams) => {
    setIsRefreshing(true);

    try {
      // 创建参数副本，避免修改原始对象
      const requestParams = { ...nextParams };

      // 如果 sortBy 是 bestMatch 且有 defaultSort，替换成 defaultSort
      if (requestParams.sortBy === "bestMatch" && defaultSort?.sortBy) {
        requestParams.sortBy = defaultSort.sortBy;
        requestParams.sortDirection =
          defaultSort.sortDirection?.toLowerCase() || "desc";
      }

      const data = await getProductList(type, requestParams);
      if (data && data.pageList) {
        setProductList(data.pageList.items || []);
        setCurrentPage(Number(nextParams.page) || 1);
        setTotal(data.pageList.total || 0);
        setFinished(
          (data.pageList.items?.length || 0) >= (data.pageList.total || 0)
        );
        // 请求成功后更新 queryParams 状态
        setQueryParams(nextParams);
      } else {
        setProductList([]);
        setTotal(0);
        setFinished(true);
        // 即使没有数据，也更新 queryParams 状态
        setQueryParams(nextParams);
      }
    } catch (error) {
      console.error("刷新列表失败:", error);
      setProductList([]);
      setTotal(0);
      setFinished(true);
      // 即使出错，也更新 queryParams 状态
      setQueryParams(nextParams);
    } finally {
      setIsRefreshing(false);
    }
  };

  // 切换展示模式
  const toggleDisplayMode = () => {
    setIsGridMode(!isGridMode);
  };

  // 处理筛选变化的回调函数（单选）：任何筛选或排序都会覆盖之前相同类型的值
  const handleSortChange = (option: {
    type: string;
    value?: string;
    item?: FilterOptionItem;
  }) => {
    // 直接基于当前的 queryParams 构建新参数
    const nextParams: CategoryQueryParams = { ...queryParams };

    switch (option.type) {
      case "category": {
        // 分类筛选：直接更新参数并刷新列表
        // 清除其他筛选类型的参数，实现单选
        delete nextParams.minPrice;
        delete nextParams.maxPrice;
        delete nextParams.priceRange;
        delete nextParams.brand;
        delete nextParams.freeShipping;
        
        const categoryID = option?.item?.categoryID || "";
        if (categoryID) {
          nextParams.categoryID = categoryID;
        } else {
          delete nextParams.categoryID;
        }
        nextParams.page = 1;
        break;
      }
      case "price-range": {
        // 直接使用 item 上的 minPrice/maxPrice（单选覆盖）
        // 清除其他筛选类型的参数，实现单选
        delete nextParams.categoryID; // 清除分类筛选
        delete nextParams.priceRange;
        delete nextParams.minPrice;
        delete nextParams.maxPrice;
        delete nextParams.brand; // 清除品牌筛选
        delete nextParams.freeShipping; // 清除免邮筛选
        // 清除排序，筛选时使用默认排序
        delete nextParams.sortBy;
        delete nextParams.sortDirection;

        const itemMin = option?.item?.minPrice;
        const itemMax = option?.item?.maxPrice;

        if (typeof itemMin === "number") nextParams.minPrice = itemMin;
        if (typeof itemMax === "number") nextParams.maxPrice = itemMax;
        nextParams.page = 1;
        break;
      }
      case "hot-brand": {
        // 清除其他筛选类型的参数，实现单选
        delete nextParams.categoryID; // 清除分类筛选
        delete nextParams.minPrice;
        delete nextParams.maxPrice;
        delete nextParams.priceRange;
        delete nextParams.freeShipping; // 清除免邮筛选
        // 清除排序，筛选时使用默认排序
        delete nextParams.sortBy;
        delete nextParams.sortDirection;

        let brandValue = "";
        if (option?.item?.name) {
          brandValue = option.item.name;
        } else if (option?.value) {
          brandValue = option.value;
        }

        if (brandValue === "") {
          delete nextParams.brand;
        } else {
          nextParams.brand = brandValue;
        }
        nextParams.page = 1;
        break;
      }
      case "filter": {
        // 目前仅支持免邮筛选
        // 清除其他筛选类型的参数，实现单选
        delete nextParams.categoryID; // 清除分类筛选
        delete nextParams.brand;
        delete nextParams.minPrice;
        delete nextParams.maxPrice;
        delete nextParams.priceRange;

        const filterValue = option?.value;
        if (filterValue === "freeshipping") {
          nextParams.freeShipping = 1;
        } else {
          delete nextParams.freeShipping;
        }
        nextParams.page = 1;
        break;
      }
      case "popularity": {
        // 侧边栏的"Popularity"快捷项
        // 清除所有筛选条件，排序时只保留排序
        delete nextParams.categoryID; // 清除分类筛选
        delete nextParams.brand;
        delete nextParams.minPrice;
        delete nextParams.maxPrice;
        delete nextParams.priceRange;
        delete nextParams.freeShipping;

        nextParams.sortBy = "popularity";
        nextParams.sortDirection = "desc";
        nextParams.page = 1;
        break;
      }
      case "sort": {
        // 顶部四个排序按钮：bestMatch / price-low-to-high / price-high-to-low / new-arrivals
        // 清除所有筛选条件，排序时只保留排序
        delete nextParams.categoryID; // 清除分类筛选
        delete nextParams.brand;
        delete nextParams.minPrice;
        delete nextParams.maxPrice;
        delete nextParams.priceRange;
        delete nextParams.freeShipping;

        if (option.value === "bestMatch") {
          nextParams.sortBy = "bestMatch";
          nextParams.sortDirection = "desc";
        } else if (option.value === "price-low-to-high") {
          nextParams.sortBy = "price";
          nextParams.sortDirection = "asc";
        } else if (option.value === "price-high-to-low") {
          nextParams.sortBy = "price";
          nextParams.sortDirection = "desc";
        } else if (option.value === "new-arrivals") {
          // 后端按需调整字段名
          nextParams.sortBy = "addTime";
          nextParams.sortDirection = "desc";
        } else if (!option.value) {
          nextParams.sortBy = "bestMatch";
          nextParams.sortDirection = "desc";
        }
        nextParams.page = 1;
        break;
      }
      default:
        break;
    }

    // 如果没有设置排序，且有默认排序，使用默认排序（仅在筛选时）
    if (!nextParams.sortBy && defaultSort?.sortBy) {
      nextParams.sortBy = defaultSort.sortBy;
      nextParams.sortDirection =
        defaultSort.sortDirection?.toLowerCase() || "desc";
    }

    // 如果 sortBy 是 bestMatch 且有 defaultSort，替换成 defaultSort
    if (nextParams.sortBy === "bestMatch" && defaultSort?.sortBy) {
      nextParams.sortBy = defaultSort.sortBy;
      nextParams.sortDirection =
      defaultSort.sortDirection?.toLowerCase() || "desc";
    }

    // 刷新列表（refreshList 成功后会自动更新 queryParams）
    refreshList(nextParams);
  };

  return (
    <div className={styles["comp-category-content"]} style={{backgroundColor: "#F2F2F2"}}>
      <PageTheme footerTheme="white" />
      <div className={styles["content-title"]}>
        <div className={styles["category-name"]}>
          {title || typeTitleMap[type] || type}
          {/* 展示数量 */}
          {total > 0 && <span>&nbsp;({total})</span>}
        </div>
        <Image
          src={`${process.env.IMAGE_DOMAIN}/svg/category-page/${
            isGridMode ? "toggle-column.svg" : "toggle-grid.svg"
          }`}
          alt="Grid"
          className={styles["display-toggle"]}
          onClick={toggleDisplayMode}
          width={20}
          height={20}
        />
      </div>

      <FilterBox
        onSortChange={handleSortChange}
        sidebarFilter={localSidebarFilter}
        {...(type === "free-shipping" && { showFreeShipping: false })}
        defaultSort={defaultSort}
      />

      {/* 产品列表 */}
      <div className={styles["product-list-container"]}>
        {isInitialLoading ? (
          // 初始加载时显示骨架屏
          <div
            className={styles["skeleton-box"]}
            style={{
              minHeight: "40vh",
              paddingTop: "40px",
              backgroundColor: "#fff",
            }}
          >
            <Skeleton />
            <Skeleton style={{ marginTop: "28px" }} />
            <Skeleton style={{ marginTop: "28px" }} />
          </div>
        ) : (
          <>
            {/* 筛选/排序时的加载蒙层 */}
            {isRefreshing && (
              <div className={styles["loading-overlay"]}>
                <Loading color="#e6415f" style={{ marginTop: "100px" }} />
              </div>
            )}

            <List
              finished={finished}
              onLoad={onLoad}
              loadingText={<InfiniteListLoading />}
              finishedText={productList.length > 0 ? "Finished !" : ""}
              errorText={"Load Error !"}
            >
              {productList.length > 0 ? (
                <div
                  className={
                    isGridMode
                      ? styles["product-list"]
                      : styles["product-list-full"]
                  }
                >
                  {productList.map((product: ProductType, index: number) => (
                    <div
                      className={
                        isGridMode
                          ? styles["product-item"]
                          : styles["product-item-full"]
                      }
                      key={`${product.productID}-${index}`}
                    >
                      {isGridMode ? (
                        <ProductItem product={product} {...(type === "clearance" && { isClearance: true })}
                        />
                      ) : (
                        <ProductCardFullWidth product={product} {...(type === "clearance" && { isClearance: true })} />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                finished && (
                  <div className={styles["empty-list"]}>No item found.</div>
                )
              )}
            </List>
          </>
        )}
      </div>
    </div>
  );
}
